import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, MessageSquare, HeartPulse, Users, Clock } from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { V } from "@/lib/vocab";

const chartsFactory = () => [
  { key: "conversations", title: "Conversations handled", sub: `Total ${V.lc("person")} conversations the concierge handled`, color: "#e5342e", icon: MessageSquare, type: "area" },
  { key: "sentiment", title: `${V.one("person")} sentiment`, sub: "Overall sentiment score (0–100)", color: "#0d9488", icon: HeartPulse, type: "line", domain: [0, 100], unit: "/100" },
  { key: "active_residents", title: `${V.many("person")} participating`, sub: `${V.many("person")} who texted, called or signed in`, color: "#4f46e5", icon: Users, type: "area" },
  { key: "hours_saved", title: "Staff hours saved", sub: "Person-hours the concierge saved your team", color: "#d97706", icon: Clock, type: "area", unit: "h" },
];

const fmt = (v, unit) => (v === null || v === undefined ? "—" : `${v}${unit || ""}`);
const compact = (n) => {
  if (n === null || n === undefined) return "";
  const a = Math.abs(n);
  if (a >= 1000) return `${(n / 1000).toFixed(a >= 10000 ? 0 : 1)}k`;
  return `${n}`;
};

function TrendTooltip({ active, payload, unit }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-sand-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-bold text-ink">{fmt(payload[0].value, unit)}</div>
      <div className="text-ink/50">{p.start} → {p.end}</div>
    </div>
  );
}

function TrendCard({ meta, points, tickFmt }) {
  const Icon = meta.icon;
  const nums = points.map((p) => p[meta.key]).filter((v) => v !== null && v !== undefined);
  const hasData = nums.some((v) => v !== 0);
  const lastNz = [...nums].reverse().find((v) => v > 0);
  const last = lastNz != null ? lastNz : (nums.length ? nums[nums.length - 1] : null);
  const firstNz = nums.find((v) => v > 0);
  const base = firstNz != null ? firstNz : (nums.length ? nums[0] : null);
  const delta = (last != null && base != null && base !== 0 && last !== base)
    ? Math.round(((last - base) / Math.abs(base)) * 100) : null;
  const deltaLabel = delta == null ? null
    : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta) > 999 ? "999%+" : `${Math.abs(delta)}%`}`;
  const gid = `grad-${meta.key}`;
  const dotProps = { r: 3, fill: meta.color, strokeWidth: 0 };
  const activeDot = { r: 5, fill: meta.color, stroke: "#fff", strokeWidth: 2 };
  return (
    <Card className="p-5 transition-shadow hover:shadow-md" data-testid={`trend-card-${meta.key}`}>
      <div className="mb-3 flex items-start gap-2.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-ink">{meta.title}</div>
          <div className="text-xs text-ink/45">{meta.sub}</div>
        </div>
        {hasData && last != null && (
          <div className="ml-auto text-right shrink-0" data-testid={`trend-headline-${meta.key}`}>
            <div className="text-xl font-extrabold leading-none tabular-nums" style={{ color: meta.color }}>{fmt(last, meta.unit)}</div>
            {delta != null && (
              <div className={`mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-bold tabular-nums ${delta >= 0 ? "text-emerald-600" : "text-redline-600"}`}>
                {deltaLabel}
              </div>
            )}
          </div>
        )}
      </div>
      {!hasData ? (
        <div className="flex h-[200px] items-center justify-center text-sm text-ink/35">No data yet for this range</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          {meta.type === "line" ? (
            <LineChart data={points} margin={{ left: -4, right: 10, top: 6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DF" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8A8178" }} tickFormatter={tickFmt} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis domain={meta.domain || ["auto", "auto"]} tick={{ fontSize: 11, fill: "#8A8178" }} tickFormatter={compact} tickLine={false} axisLine={false} width={44} />
              <Tooltip content={<TrendTooltip unit={meta.unit} />} cursor={{ stroke: meta.color, strokeOpacity: 0.25, strokeWidth: 1.5 }} />
              <Line type="monotone" dataKey={meta.key} stroke={meta.color} strokeWidth={2.5} dot={dotProps} activeDot={activeDot} connectNulls={false} animationDuration={700} />
            </LineChart>
          ) : (
            <AreaChart data={points} margin={{ left: -4, right: 10, top: 6, bottom: 0 }}>
              <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={meta.color} stopOpacity={0.32} />
                  <stop offset="100%" stopColor={meta.color} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DF" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8A8178" }} tickFormatter={tickFmt} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: "#8A8178" }} tickFormatter={compact} tickLine={false} axisLine={false} width={44} allowDecimals={false} />
              <Tooltip content={<TrendTooltip unit={meta.unit} />} cursor={{ stroke: meta.color, strokeOpacity: 0.25, strokeWidth: 1.5 }} />
              <Area type="monotone" dataKey={meta.key} stroke={meta.color} strokeWidth={2.5} fill={`url(#${gid})`} dot={false} activeDot={activeDot} animationDuration={700} />
            </AreaChart>
          )}
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export function ReportTrends({ tenantId }) {
  const CHARTS = chartsFactory();
  const [granularity, setGranularity] = useState("month");
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    setLoading(true);
    api.get(`/tenants/${tenantId}/report/trends`, { params: { granularity, periods: 12 } })
      .then((r) => { if (alive) setPoints(r.data.points || []); })
      .catch(() => { if (alive) setPoints([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, granularity]);

  const tickFmt = (v) => (granularity === "month" ? String(v).split(" ")[0] : v);

  return (
    <div className="space-y-5" data-testid="report-trends">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink/55">Last 12 {granularity === "month" ? "months" : "weeks"} · updates live from your {V.lc("org")} data.</p>
        <div className="inline-flex rounded-full border border-sand-200 bg-sand-100 p-1" data-testid="trends-granularity">
          {[["month", "Monthly"], ["week", "Weekly"]].map(([k, l]) => (
            <button key={k} data-testid={`trends-granularity-${k}`} onClick={() => setGranularity(k)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${granularity === k ? "bg-ink text-white shadow-sm" : "text-ink/55 hover:text-ink"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-24 text-ink/50">
          <Loader2 className="h-5 w-5 animate-spin" /> Building trends…
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {CHARTS.map((meta) => <TrendCard key={meta.key} meta={meta} points={points} tickFmt={tickFmt} />)}
        </div>
      )}
    </div>
  );
}
